import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { FilledInput, Input as DefaultInput } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CancelIcon, SearchIcon } from '@sharedCR/coreDesignSystem/icons';
import PropType from 'prop-types';

import useFocusSearchOnType from 'hooks/useFocusSearchOnType';
import useSearch from 'hooks/useSearch';
import { checkIsLightTheme } from 'utils/theme';

const getStyles = (theme, { isFilled, color } = {}) => {
  const { palette } = theme;
  const isLightTheme = checkIsLightTheme(palette.type);
  const isPrimaryColor = color === 'primary';

  const backgroundColor =
    isPrimaryColor && !isLightTheme ? palette.primary[500] : palette.background.paper;

  return {
    searchInput: {
      '& input': {
        lineHeight: '1.5',
        fontSize: '1.4rem',
        padding: '0.4rem 0.8rem',
      },

      '& span': {
        color: '#9EAFC3',
      },

      display: 'flex',
      borderRadius: isFilled ? '0.4rem' : null,
      padding: isFilled ? '0.3rem 0.9rem' : null,
      backgroundColor: isFilled ? `${backgroundColor} !important` : null,
    },
  };
};

const searchIconStyles = {
  color: '#9EAFC3',
  width: '2rem',
  height: '2rem',
  strokeWidth: 1,
};

const SearchInput = forwardRef(
  (
    {
      isFilled = false,
      isWithIcon = false,
      isCloseIcon = false,
      value = '',
      rightSideContent,
      onChange = null,
      ariaLabel = 'Search',
      dataTestId = 'search-text',
      inputRef = null,
      focusOnType = false,
      placeholder = 'Search',
      ...restProps
    },
    ref,
  ) => {
    const theme = useTheme();
    const classes = getStyles(theme, { isFilled, color: restProps.color });
    const { searchText, setSearchText } = useSearch({ shouldResetValueOnUnmount: !onChange });

    const { disabled } = restProps;
    const internalInputRef = useRef(null);

    const Input = isFilled ? FilledInput : DefaultInput;

    useImperativeHandle(ref, () => ({ focus: () => internalInputRef.current?.focus() }), []);

    const inputProps = useMemo(
      () => ({
        ...restProps.inputProps,
        'aria-label': ariaLabel,
      }),
      [restProps.inputProps, ariaLabel],
    );

    const onSearchText = useCallback(e => setSearchText(e.target.value), [setSearchText]);

    const onClear = useCallback(
      () => (onChange ? onChange({ target: { value: '' } }) : setSearchText('')),
      [onChange, setSearchText],
    );

    const onType = useCallback(
      char => {
        if (disabled) {
          return;
        }

        if (onChange) {
          onChange({ target: { value: char } });

          return;
        }

        setSearchText(char);
      },
      [disabled, onChange, setSearchText],
    );

    useFocusSearchOnType({ enabled: focusOnType && !disabled, inputRef: internalInputRef, onType });

    return (
      <Input
        {...restProps}
        className={restProps.className}
        data-testid={dataTestId}
        disableUnderline={isFilled || restProps.disableUnderline}
        endAdornment={
          <>
            {rightSideContent}
            {isCloseIcon && <CancelIcon className="mr-8 cursor-pointer" onClick={onClear} />}
          </>
        }
        inputProps={inputProps}
        inputRef={internalInputRef}
        margin="dense"
        placeholder={placeholder}
        ref={inputRef}
        startAdornment={isWithIcon && <SearchIcon className="mr-8" style={searchIconStyles} />}
        sx={classes.searchInput}
        value={onChange ? value : searchText}
        onChange={onChange || onSearchText}
      />
    );
  },
);

SearchInput.propTypes = {
  isFilled: PropType.bool,
  value: PropType.string,
  placeholder: PropType.string,
  className: PropType.string,
  color: PropType.string,
  disabled: PropType.bool,
  isWithIcon: PropType.bool,
  isCloseIcon: PropType.bool,
  focusOnType: PropType.bool,
  rightSideContent: PropType.node,
  dataTestId: PropType.string,
  ariaLabel: PropType.string,
  inputRef: PropType.oneOfType([PropType.func, PropType.shape({ current: PropType.object })]),
  onChange: PropType.func,
};

export default SearchInput;
